import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FiMail, FiUpload, FiSend, FiEye, FiFile, FiX, FiLogOut, FiLock, FiShield, FiUser, FiCpu, FiClock, FiUsers, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  startMicrosoftAuth,
  handleMicrosoftCallback,
  getValidAccessToken,
  clearMicrosoftTokens,
} from '../utils/microsoftAuth';
import { handleSessionExpired, isTokenExpiredError } from '../utils/auth';
import TemplateSelector from './TemplateSelector';
import EmailPreview from './EmailPreview';
import TemplateEditor from './TemplateEditor';
import AIEmailHelper from './AIEmailHelper';
import RichTextBody from './RichTextBody';

const getTimezones = () => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto', 'Australia/Sydney', 'Pacific/Auckland'];
  }
};

const formatTime = (date, tz) => {
  return new Date(date).toLocaleString('en-GB', {
    timeZone: tz,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const RecallToast = ({ t, seconds, queueId, onRecalled }) => {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    const timer = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) clearInterval(timer);
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRecall = async () => {
    try {
      await axios.post(`/api/scheduler/queue/${queueId}/recall`);
      onRecalled();
      toast.dismiss(t.id);
      toast.success('Email recalled successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not recall');
    }
  };

  return (
    <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
      <div className="flex-1 w-0 p-4">
        <div className="flex items-start">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Email queued</p>
            <p className="mt-1 text-sm text-gray-500">
              Sending in {Math.max(0, remaining)}s. You can still recall it.
            </p>
          </div>
        </div>
      </div>
      <div className="flex border-l border-gray-200">
        <button
          onClick={handleRecall}
          className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
        >
          Recall
        </button>
      </div>
    </div>
  );
};

const EmailComposer = () => {
  const location = useLocation();
  const [fromEmail, setFromEmail] = useState('');
  const [toEmails, setToEmails] = useState([]); // Array of {email, name} objects
  const [toEmailInput, setToEmailInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [accessToken, setAccessToken] = useState('');       // Microsoft token
  const [googleAccessToken, setGoogleAccessToken] = useState(''); // Gmail token
  const [provider, setProvider] = useState('microsoft');    // 'microsoft' | 'gmail'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showAIHelper, setShowAIHelper] = useState(false);
  const [showSendWarning, setShowSendWarning] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showStaggeredModal, setShowStaggeredModal] = useState(false);
  const [staggeredStart, setStaggeredStart] = useState('');
  const [staggeredInterval, setStaggeredInterval] = useState(5);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsSearch, setContactsSearch] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [recipientTitle, setRecipientTitle] = useState('');
  const [recipientLocation, setRecipientLocation] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const recallWindowSeconds = 60;
  // Role-based access control
  const [userEmail, setUserEmail] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userRole, setUserRole] = useState(null); // 'admin' | 'user' | null (not loaded yet)
  const [itsmLoading, setItsmLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
    // Restore Microsoft token (will be validated/refreshed by the /me fetch effect)
    const msToken = localStorage.getItem('ms_access_token');
    if (msToken) {
      setAccessToken(msToken);
    }
    // Restore Google token
    const googleToken = localStorage.getItem('google_access_token');
    if (googleToken) {
      setGoogleAccessToken(googleToken);
    }
    // Restore active provider
    const savedProvider = localStorage.getItem('active_provider');
    if (savedProvider) {
      setProvider(savedProvider);
    }
    // Restore cached user info
    const cachedEmail = localStorage.getItem('ms_user_email');
    const cachedRole = localStorage.getItem('ms_user_role');
    const cachedName = localStorage.getItem('ms_user_name');
    if (cachedEmail && cachedRole) {
      setUserEmail(cachedEmail);
      setUserRole(cachedRole);
      setUserDisplayName(cachedName || cachedEmail);
      setFromEmail(cachedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user profile + role whenever the access token is set
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const fetchUserRole = async () => {
      try {
        const response = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        const { email, displayName, role } = response.data;
        setUserEmail(email);
        setUserDisplayName(displayName);
        setUserRole(role);
        // Cache for page refreshes
        localStorage.setItem('ms_user_email', email);
        localStorage.setItem('ms_user_role', role);
        localStorage.setItem('ms_user_name', displayName);
        // Lock from-email to signed-in account
        setFromEmail(email);
        toast(`Signed in as ${displayName}${role === 'admin' ? ' (Admin)' : ''}`, { icon: role === 'admin' ? '🛡️' : '👤' });
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching user role:', error);
        // If the token is expired/unauthorized, attempt a silent refresh once.
        // If refresh fails, handleSessionExpired() will auto-logout.
        if (isTokenExpiredError(error)) {
          const refreshed = await getValidAccessToken();
          if (refreshed && !cancelled) {
            setAccessToken(refreshed);
          }
          // If refresh returned null, handleSessionExpired already ran inside refreshAccessToken
        } else {
          // Non-auth error — keep app usable with cached role
          setUserRole(localStorage.getItem('ms_user_role') || 'admin');
        }
      }
    };
    fetchUserRole();
    return () => { cancelled = true; };
  }, [accessToken]);

  const loadContacts = async () => {
    try {
      const userEmail = (localStorage.getItem('ms_user_email') || '').toLowerCase();
      const params = userEmail ? { user: userEmail } : {};
      const { data } = await axios.get('/api/contacts', { params });
      setContacts(data.contacts || []);
    } catch (error) {
      toast.error('Could not load contacts: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOpenItsm = async () => {
    const email = (localStorage.getItem('ms_user_email') || userEmail || '').trim();
    const name = (localStorage.getItem('ms_user_name') || userDisplayName || '').trim();
    if (!email || !name) {
      toast.error('Please sign in first to open ITSM.');
      return;
    }
    setItsmLoading(true);
    try {
      const { data } = await axios.post('/api/itsm/redirect', { email, name });
      if (data.redirectUrl) {
        window.open(data.redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('ITSM redirect URL was not returned.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not open ITSM.');
    } finally {
      setItsmLoading(false);
    }
  };

  const openContactsModal = () => {
    setContactsSearch('');
    setSelectedContactIds(new Set());
    loadContacts();
    setShowContactsModal(true);
  };

  const toggleContactSelection = (id) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applySelectedContacts = () => {
    const selected = contacts.filter((c) => selectedContactIds.has(c.id));
    const existingEmails = new Set(toEmails.map((r) => (typeof r === 'string' ? r : r.email).toLowerCase()));
    const newRecipients = selected
      .filter((c) => c.email && !existingEmails.has(c.email.toLowerCase()))
      .map((c) => ({ email: c.email, name: c.name || null }));
    if (newRecipients.length > 0) {
      setToEmails([...toEmails, ...newRecipients]);
      toast.success(`Added ${newRecipients.length} contact(s)`);
    } else {
      toast('No new contacts to add');
    }
    setShowContactsModal(false);
  };

  // Receive contacts sent from the Contacts page
  useEffect(() => {
    const selected = location.state?.selectedContacts;
    if (selected && Array.isArray(selected) && selected.length > 0) {
      const mapped = selected.map(c => ({
        email: c.email,
        name: c.name || '',
        title: c.title || '',
        company: c.company || '',
        location: c.location || '',
        phone: c.phone || '',
      }));
      setToEmails(mapped);
      if (mapped[0].title) setRecipientTitle(mapped[0].title);
      if (mapped[0].location) setRecipientLocation(mapped[0].location);
      toast.success(`Loaded ${mapped.length} contact(s) from Contacts`);
      // Clear the navigation state so a refresh doesn't re-add them
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get('/api/templates');
      const loadedTemplates = response.data;
      setTemplates(loadedTemplates);
      
      // If we have a selected template, update it with the latest version
      if (selectedTemplate) {
        const updatedTemplate = loadedTemplates.find(t => t.id === selectedTemplate.id);
        if (updatedTemplate) {
          setSelectedTemplate(updatedTemplate);
          // Update body if template editor is not showing
          if (!showTemplateEditor) {
            setBody(updatedTemplate.content || '');
          }
        }
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleMicrosoftAuth = () => {
    startMicrosoftAuth();
  };

  const handleGoogleAuth = () => {
    localStorage.setItem('pending_provider', 'gmail');
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your_google_client_id';
    const redirectUri = encodeURIComponent(window.location.origin);
    const scopes = encodeURIComponent('https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly email profile');
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scopes}&prompt=select_account`;
    window.location.href = authUrl;
  };

  const fetchGoogleUserRole = async (token) => {
    try {
      const response = await axios.get('/api/auth/google/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { email, displayName, role } = response.data;
      setUserEmail(email);
      setUserDisplayName(displayName);
      setUserRole(role);
      setFromEmail(email); // Gmail always sends from the signed-in address
      localStorage.setItem('ms_user_email', email);
      localStorage.setItem('ms_user_role', role);
      localStorage.setItem('ms_user_name', displayName);
      if (role === 'user') {
        toast(`Signed in as ${displayName} (Gmail)`, { icon: '📧' });
      } else {
        toast(`Signed in as ${displayName} — Gmail Admin`, { icon: '🛡️' });
      }
    } catch (error) {
      console.error('Error fetching Google user role:', error);
      setUserRole('admin');
    }
  };

  useEffect(() => {
    // Handle OAuth callback for both Microsoft (Auth Code + PKCE) and Google (Implicit)
    const pendingProvider = localStorage.getItem('pending_provider') || 'microsoft';

    // ── Microsoft: Auth Code + PKCE flow (response_mode=query → ?code=...) ──
    const queryParams = new URLSearchParams(window.location.search);
    const authCode = queryParams.get('code');
    const authError = queryParams.get('error');
    if (authError) {
      const errorDesc = queryParams.get('error_description') || authError;
      queryParams.delete('error'); queryParams.delete('error_description');
      window.history.replaceState({}, document.title, window.location.pathname);
      toast.error(`Microsoft sign-in failed: ${errorDesc}`, { duration: 6000 });
      return;
    }
    if (authCode && pendingProvider === 'microsoft') {
      localStorage.removeItem('pending_provider');
      window.history.replaceState({}, document.title, window.location.pathname);
      (async () => {
        try {
          const token = await handleMicrosoftCallback(authCode);
          setAccessToken(token);
          setProvider('microsoft');
          // Clear Gmail session when switching to Microsoft
          setGoogleAccessToken('');
          localStorage.removeItem('google_access_token');
          toast.success('Successfully authenticated with Microsoft!');
        } catch (err) {
          console.error('Microsoft callback exchange failed:', err);
          toast.error('Microsoft sign-in failed: ' + (err.response?.data?.error || err.message), { duration: 6000 });
        }
      })();
      return;
    }

    // ── Google: Implicit flow (response_mode=fragment → #access_token=...) ──
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');
      if (token) {
        localStorage.removeItem('pending_provider');
        window.history.replaceState({}, document.title, window.location.pathname);

        if (pendingProvider === 'gmail') {
          setGoogleAccessToken(token);
          setProvider('gmail');
          localStorage.setItem('google_access_token', token);
          localStorage.setItem('active_provider', 'gmail');
          // Clear Microsoft session when switching to Gmail
          setAccessToken('');
          clearMicrosoftTokens();
          toast.success('Successfully authenticated with Google!');
          fetchGoogleUserRole(token);
        }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      setAccessToken('');
      setGoogleAccessToken('');
      setProvider('microsoft');
      setUserEmail('');
      setUserRole(null);
      setUserDisplayName('');
      setFromEmail('');
      clearMicrosoftTokens();
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('active_provider');
      localStorage.removeItem('ms_user_email');
      localStorage.removeItem('ms_user_role');
      localStorage.removeItem('ms_user_name');
      toast.success('Signed out successfully');
    }
  };

  const handleAddEmail = () => {
    const email = toEmailInput.trim();
    if (email && isValidEmail(email)) {
      const emailLower = email.toLowerCase();
      const exists = toEmails.some(r => 
        (typeof r === 'string' ? r : r.email) === emailLower
      );
      if (!exists) {
        setToEmails([...toEmails, { 
          email: emailLower, 
          name: extractNameFromEmail(emailLower) 
        }]);
        setToEmailInput('');
      } else {
        toast.error('Email already added');
      }
    } else if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
    }
  };

  const extractNameFromEmail = (email) => {
    const parts = email.split('@')[0];
    const nameParts = parts.split(/[._-]/);
    return nameParts
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setToEmails(toEmails.filter(r => {
      const email = typeof r === 'string' ? r : r.email;
      return email !== emailToRemove;
    }));
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Helper function to check if body has meaningful content
  const hasBodyContent = (bodyText) => {
    if (!bodyText || bodyText.trim() === '') return false;
    // body is now the section text (or plain text) — strip any HTML tags and check for real content
    const textOnly = bodyText.replace(/<[^>]*>/g, '').trim();
    return textOnly.length > 0;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/upload/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Handle both old format (emails array) and new format (recipients array)
      const recipients = response.data.recipients || 
        (response.data.emails || []).map(email => ({ email, name: extractNameFromEmail(email) }));
      
      // Build a quick lookup: email → Excel name (so we can update existing recipients too)
      const excelNameMap = {};
      recipients.forEach(r => {
        const em = typeof r === 'string' ? r : r.email;
        const nm = typeof r === 'object' ? r.name : null;
        if (em && nm) excelNameMap[em.toLowerCase()] = nm;
      });

      // Update existing recipients: if the Excel provides a real name and the current
      // name looks like an email-prefix derivation, replace it with the Excel name.
      const updatedExisting = toEmails.map(r => {
        const em = (typeof r === 'string' ? r : r.email).toLowerCase();
        const excelName = excelNameMap[em];
        if (!excelName) return r;
        const currentName = typeof r === 'object' ? (r.name || '') : '';
        // Replace if current name is empty or matches what extractNameFromEmail would produce
        const derivedName = extractNameFromEmail(em);
        if (!currentName || currentName === derivedName) {
          return typeof r === 'string' ? { email: r, name: excelName } : { ...r, name: excelName };
        }
        return r;
      });

      const existingEmailSet = new Set(updatedExisting.map(r => (typeof r === 'string' ? r : r.email).toLowerCase()));
      const newRecipients = recipients.filter(r => {
        const email = (typeof r === 'string' ? r : r.email).toLowerCase();
        return !existingEmailSet.has(email);
      });
      
      setToEmails([...updatedExisting, ...newRecipients]);
      toast.success(`Added ${newRecipients.length} recipient(s) from file`);
    } catch (error) {
      toast.error('Error parsing file: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = async (template) => {
    setSelectedTemplate(template);
    // Clear any carryover from a previous template session
    setBody('');
    setRecipientTitle('');
    setRecipientLocation('');
    // Extract subject without placeholders for display
    const subjectText = template.subject || '';
    const cleanSubject = subjectText.replace(/\{\{[^}]+\}\}/g, '').trim();
    setSubject(cleanSubject || 'Email Subject');
    
    // Set body to the body-section VALUE only (not the full template HTML).
    // The template HTML wrapper (header, image, CTA, footer) is stored on the server
    // and applied via templateId at send/preview time by replacing {{body}}.
    const bodySection = template.sections?.find(s => s.id === 'body' && s.type === 'textarea');
    setBody(bodySection?.value || '');

    // Show template editor
    setShowTemplateEditor(true);
    toast.success(`Template "${template.name}" loaded - Edit sections below`);
  };

  const handleTemplateUpdate = async (updatedTemplate) => {
    // Set body to the body-section VALUE only (the text that fills {{body}} in the template).
    // The full template HTML (header, image, CTA, footer) lives on the server and is applied
    // via templateId at send/preview time.
    const bodySection = updatedTemplate.sections?.find(s => s.id === 'body' && s.type === 'textarea');
    const newBody = bodySection?.value || '';
    
    const hasContent = newBody.trim().length > 0;
    
    setSelectedTemplate(updatedTemplate);
    setBody(newBody);
    setShowTemplateEditor(false);
    
    // Reload templates to get the updated one from server
    await loadTemplates();
    
    if (!hasContent) {
      toast.error('Warning: Email body appears empty. Please add content in the Email Content section.');
    } else {
      toast.success('Template saved! Ready to send emails.');
    }
  };

  const handlePreview = async () => {
    if (!subject || !body) {
      toast.error('Please fill in subject and body');
      return;
    }
    setShowPreview(true);
  };

  const getFirstRecipient = () => {
    if (toEmails.length === 0) return { email: 'example@email.com', name: null };
    const first = toEmails[0];
    return typeof first === 'string' 
      ? { email: first, name: extractNameFromEmail(first) }
      : first;
  };

  const handleSendClick = () => {
    setShowSendWarning(true);
    if (!fromEmail || !isValidEmail(fromEmail)) {
      toast.error('Please enter a valid from email address');
      return;
    }
    if (toEmails.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (!subject || !body) {
      toast.error('Please fill in subject and body');
      return;
    }
    const activeToken = provider === 'gmail' ? googleAccessToken : accessToken;
    if (!activeToken) {
      toast.error('Please sign in with Microsoft or Google first');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleStaggeredSendClick = () => {
    setShowSendWarning(true);
    if (!fromEmail || !isValidEmail(fromEmail)) {
      toast.error('Please enter a valid from email address');
      return;
    }
    if (toEmails.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (!subject || !body) {
      toast.error('Please fill in subject and body');
      return;
    }
    const activeToken = provider === 'gmail' ? googleAccessToken : accessToken;
    if (!activeToken) {
      toast.error('Please sign in with Microsoft or Google first');
      return;
    }
    // Default start time to next round 5-minute mark
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5 - (now.getMinutes() % 5), 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    setStaggeredStart(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
    setShowStaggeredModal(true);
  };

  const handleConfirmStaggeredSend = async () => {
    if (!staggeredStart) {
      toast.error('Please select a start time');
      return;
    }
    setShowStaggeredModal(false);
    setIsLoading(true);
    try {
      // For Microsoft, ensure we have a fresh (non-expired) token before queueing.
      // For Gmail, use the stored token as before.
      let activeToken = provider === 'gmail' ? googleAccessToken : accessToken;
      if (provider !== 'gmail') {
        activeToken = await getValidAccessToken();
        if (activeToken) setAccessToken(activeToken);
      }
      if (!activeToken) {
        toast.error('Please sign in again to continue');
        return;
      }
      const recipients = toEmails.map((r) => (typeof r === 'string' ? { email: r, name: null } : r));
      const { data } = await axios.post('/api/scheduler/batch', {
        name: subject,
        from_email: fromEmail,
        recipients,
        subject,
        body: body,
        template_id: selectedTemplate?.id,
        provider,
        access_token: activeToken,
        timezone,
        start_at: new Date(staggeredStart).toISOString(),
        interval_minutes: staggeredInterval,
      });
      const intervalText = staggeredInterval > 0 ? `every ${staggeredInterval} minutes` : 'all at once';
      toast.success(`Scheduled ${data.count} emails ${intervalText} starting ${new Date(staggeredStart).toLocaleString()}`);
    } catch (error) {
      if (isTokenExpiredError(error)) {
        handleSessionExpired('Your Microsoft session has expired. Please sign in again.');
      } else {
        toast.error('Staggered scheduling failed: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSend = async () => {
    setShowConfirmModal(false);
    // For Microsoft, ensure we have a fresh (non-expired) token before queueing.
    // For Gmail, use the stored token as before.
    let activeToken = provider === 'gmail' ? googleAccessToken : accessToken;
    if (provider !== 'gmail') {
      activeToken = await getValidAccessToken();
      if (activeToken) setAccessToken(activeToken);
    }
    if (!activeToken) {
      toast.error('Please sign in again to continue');
      return;
    }

    const recipients = toEmails.map(r => {
      if (typeof r === 'string') return { email: r, name: null };
      return {
        email: r.email,
        name: r.name || null,
        recipientTitle: r.title || r.recipientTitle || recipientTitle || null,
        recipientLocation: r.location || r.recipientLocation || recipientLocation || null,
      };
    });

    // Queue with a recall window (e.g. 30s from now)
    const sendAfter = new Date(Date.now() + recallWindowSeconds * 1000).toISOString();
    let queueId = null;

    try {
      const response = await axios.post('/api/scheduler/queue', {
        from_email: fromEmail,
        recipients,
        subject,
        body: body,
        template_id: selectedTemplate?.id,
        provider,
        access_token: activeToken,
        timezone,
        send_after: sendAfter,
      });
      queueId = response.data.id;
    } catch (error) {
      if (isTokenExpiredError(error)) {
        handleSessionExpired('Your Microsoft session has expired. Please sign in again.');
        return;
      }
      const errorMessage = error.response?.data?.error || error.message || 'Failed to queue email';
      toast.error(errorMessage, { duration: 5000 });
      return;
    }

    // Show recall toast with live countdown
    let recalled = false;
    const toastId = toast.custom(
      (t) => <RecallToast t={t} seconds={recallWindowSeconds} queueId={queueId} onRecalled={() => { recalled = true; }} />,
      { duration: recallWindowSeconds * 1000, position: 'top-center' }
    );

    // After recall window, report status
    setTimeout(async () => {
      if (recalled) return;
      try {
        const statusRes = await axios.get(`/api/scheduler/queue/${queueId}`);
        if (statusRes.data.status === 'sent') {
          toast.success('Email sent successfully!', { id: toastId });
        } else if (statusRes.data.status === 'recalled') {
          toast.success('Email was recalled', { id: toastId });
        } else if (statusRes.data.status === 'failed') {
          toast.error(`Send failed: ${statusRes.data.error || 'unknown error'}`, { id: toastId });
        } else {
          toast.success('Email queued for sending', { id: toastId });
        }
      } catch {
        toast.success('Email queued for sending', { id: toastId });
      }
      // Clear form only when we are past the recall window and not recalled
      if (!recalled) {
        setToEmails([]);
        setSubject('');
        setBody('');
        setSelectedTemplate(null);
        setUploadedFile(null);
      }
    }, recallWindowSeconds * 1000);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Professional Header */}
      <div className="bg-white border-b border-neutral-200 shadow-soft">
        <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-accent-600 rounded-xl flex items-center justify-center shadow-medium">
                <FiMail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900">Bulk Mail Sender</h1>
                <p className="text-sm text-neutral-500">Professional email campaigns</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Show sign-in buttons only when NOT authenticated */}
              {!accessToken && !googleAccessToken && (
                <>
                  <button
                    onClick={handleMicrosoftAuth}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200 font-semibold shadow-medium hover:shadow-large transform hover:-translate-y-0.5 text-sm"
                  >
                    {/* Microsoft logo */}
                    <svg width="16" height="16" viewBox="0 0 21 21" className="flex-shrink-0">
                      <rect x="0"  y="0"  width="10" height="10" fill="#f25022"/>
                      <rect x="11" y="0"  width="10" height="10" fill="#7fba00"/>
                      <rect x="0"  y="11" width="10" height="10" fill="#00a4ef"/>
                      <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
                    </svg>
                    Sign in with Microsoft
                  </button>
                  <button
                    onClick={handleGoogleAuth}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 hover:border-neutral-400 transition-all duration-200 font-semibold shadow-medium hover:shadow-large transform hover:-translate-y-0.5 text-sm"
                  >
                    {/* Google "G" icon */}
                    <svg width="16" height="16" viewBox="0 0 48 48" className="flex-shrink-0">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    Sign in with Google
                  </button>
                </>
              )}

              {/* Show user info + nav when authenticated (either provider) */}
              {(accessToken || googleAccessToken) && (
                <>
                  {/* User info + role badge */}
                  <div className="flex items-center gap-2">
                    {/* Provider badge */}
                    {provider === 'gmail' ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-success-50 border border-success-200 rounded-lg">
                        <svg width="14" height="14" viewBox="0 0 48 48" className="flex-shrink-0">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                        <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                        <span className="text-success-700 font-medium text-sm">
                          {userDisplayName || 'Gmail User'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-2 bg-success-50 border border-success-200 rounded-lg">
                        <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                        <span className="text-success-700 font-medium text-sm">
                          {userDisplayName || 'Authenticated'}
                        </span>
                      </div>
                    )}
                    {userRole === 'admin' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-primary-50 border border-primary-200 text-primary-700 rounded-lg text-xs font-semibold">
                        <FiShield className="w-3.5 h-3.5" /> Admin
                      </span>
                    )}
                    {userRole === 'user' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-neutral-100 border border-neutral-200 text-neutral-600 rounded-lg text-xs font-semibold">
                        <FiUser className="w-3.5 h-3.5" /> User
                      </span>
                    )}
                  </div>
                  <a
                    href="/history"
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    History
                  </a>
                  <a
                    href="/inbox"
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    Inbox
                  </a>
                  <a
                    href="/tracking"
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    Tracking
                  </a>
                  <a
                    href="/contacts"
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    Contacts
                  </a>
                  <a
                    href="/scheduled"
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    Scheduled
                  </a>
                  <a
                    href="/whatsapp"
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    WhatsApp
                  </a>
                  <button
                    onClick={handleOpenItsm}
                    disabled={itsmLoading}
                    className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Open ITSM Support in a new tab"
                  >
                    <FiExternalLink className="w-4 h-4" />
                    {itsmLoading ? 'Opening…' : 'Support'}
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="px-4 py-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm"
                    title={`Sign out from ${provider === 'gmail' ? 'Google' : 'Microsoft'}`}
                  >
                    <FiLogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <div className="bg-white rounded-2xl shadow-large border border-neutral-200 overflow-hidden">
          <div className="p-8">

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Main Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* From Email */}
                <div className={`rounded-xl p-6 border ${(accessToken || googleAccessToken) ? 'bg-neutral-100 border-neutral-200' : 'bg-neutral-50 border-neutral-200'}`}>
                  <label className="block text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                    From Email Address <span className="text-red-500">*</span>
                    {(accessToken || googleAccessToken) && (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-neutral-500 bg-neutral-200 px-2 py-0.5 rounded-md">
                        <FiLock className="w-3 h-3" /> Locked to your account
                      </span>
                    )}
                  </label>
                  {(accessToken || googleAccessToken) ? (
                    // Non-editable — locked to the signed-in account email
                    <div className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-lg flex items-center gap-2 text-neutral-700 select-none">
                      <FiLock className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                      <span className="font-medium">{fromEmail}</span>
                    </div>
                  ) : (
                    // Editable when not signed in
                    <input
                      type="email"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                      placeholder="your-email@domain.com"
                      className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 text-neutral-900 placeholder-neutral-400"
                      required
                    />
                  )}
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-neutral-600 mb-1.5 flex items-center gap-2">
                      <FiClock className="w-3.5 h-3.5" /> Sender Time Zone
                    </label>
                    <select
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full max-w-sm px-3 py-2 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none text-sm text-neutral-900"
                    >
                      {getTimezones().map((tz) => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                    <p className="text-xs text-neutral-500 mt-1">
                      Current time in this zone: {formatTime(new Date(), timezone)}
                    </p>
                  </div>
                </div>

                {/* To Emails */}
                <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                  <label className="block text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                    To Email Address(es) <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="email"
                      value={toEmailInput}
                      onChange={(e) => setToEmailInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                      placeholder="Enter email and press Enter"
                      className="flex-1 px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 text-neutral-900 placeholder-neutral-400"
                    />
                    <button
                      onClick={handleAddEmail}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200 font-semibold shadow-medium hover:shadow-large transform hover:-translate-y-0.5"
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* File Upload & Contacts */}
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center px-4 py-2.5 bg-white border border-neutral-300 text-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 hover:border-primary-300 transition-all duration-200 font-medium text-sm shadow-soft">
                      <FiUpload className="mr-2 w-4 h-4" />
                      Upload File (Excel, CSV, Word)
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv,.docx,.doc,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={openContactsModal}
                      className="inline-flex items-center px-4 py-2.5 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 hover:border-primary-300 transition-all duration-200 font-medium text-sm shadow-soft"
                    >
                      <FiUsers className="mr-2 w-4 h-4" />
                      Select from Contacts
                    </button>
                    {uploadedFile && (
                      <span className="text-sm text-neutral-600 font-medium">
                        ✓ {uploadedFile.name}
                      </span>
                    )}
                  </div>

                  {/* Email Tags */}
                  {toEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 p-4 bg-white rounded-lg border border-neutral-200">
                      {toEmails.map((recipient, index) => {
                        const email = typeof recipient === 'string' ? recipient : recipient.email;
                        const name = typeof recipient === 'string' ? null : recipient.name;
                        const displayName = name && name !== email.split('@')[0].replace(/[._-]/g, ' ') 
                          ? `${name} (${email})` 
                          : email;
                        return (
                          <span
                            key={index}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium border border-primary-200 shadow-soft"
                            title={email}
                          >
                            {displayName}
                            <button
                              onClick={() => handleRemoveEmail(email)}
                              className="hover:text-red-600 transition-colors"
                            >
                              <FiX className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-sm text-neutral-500 mt-2 font-medium">
                    {toEmails.length} recipient{toEmails.length !== 1 ? 's' : ''} added
                  </p>
                </div>

                {/* Subject */}
                <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                  <label className="block text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject"
                    className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 text-neutral-900 placeholder-neutral-400"
                  />
                </div>

                {/* Vardaan UK Letter: manual fields for single send, tip for bulk */}
                {selectedTemplate?.id?.startsWith('vardaan_uk_letter') && toEmails.length <= 1 && (
                  <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200 space-y-3">
                    <label className="block text-sm font-semibold text-neutral-800 mb-1 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                      Recipient Details
                    </label>
                    <input
                      type="text"
                      value={recipientTitle}
                      onChange={(e) => setRecipientTitle(e.target.value)}
                      placeholder="Title & Organisation (e.g. CTO, Barclays Bank)"
                      className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 text-neutral-900 placeholder-neutral-400 text-sm"
                    />
                    <input
                      type="text"
                      value={recipientLocation}
                      onChange={(e) => setRecipientLocation(e.target.value)}
                      placeholder="City, Country (e.g. London, UK)"
                      className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 text-neutral-900 placeholder-neutral-400 text-sm"
                    />
                  </div>
                )}
                {selectedTemplate?.id?.startsWith('vardaan_uk_letter') && toEmails.length > 1 && (
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-xs text-indigo-800 leading-relaxed">
                    <strong className="font-semibold">💡 Bulk personalisation:</strong> Add <code className="bg-indigo-100 px-1 rounded">Title</code> and <code className="bg-indigo-100 px-1 rounded">Location</code> columns to your Excel/CSV — each recipient gets their own job title, organisation and city automatically.
                  </div>
                )}

                {/* Body */}
                <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                  <label className="block text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                    Email Body <span className="text-red-500">*</span>
                  </label>
                  {selectedTemplate && showTemplateEditor ? (
                    <TemplateEditor
                      template={selectedTemplate}
                      onUpdate={handleTemplateUpdate}
                      onClose={() => setShowTemplateEditor(false)}
                    />
                  ) : (
                    <RichTextBody
                      value={body}
                      onChange={setBody}
                      placeholder="Enter your email content. Use {{recipientName}} and {{recipientEmail}} for dynamic content."
                    />
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-2 flex-wrap">
                  {/* Preview — always visible */}
                  <button
                    onClick={handlePreview}
                    disabled={isLoading}
                    className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-all duration-200 font-medium text-sm disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <FiEye className="w-3.5 h-3.5" /> Preview
                  </button>

                  {/* AI Assist — only after login */}
                  {(accessToken || googleAccessToken) && (
                    <button
                      onClick={() => setShowAIHelper(true)}
                      disabled={isLoading}
                      className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg hover:from-violet-600 hover:to-purple-700 transition-all duration-200 font-medium text-sm disabled:opacity-50 flex items-center gap-1.5"
                      title="AI Email Assistant — check content, generate subject lines"
                    >
                      <FiCpu className="w-3.5 h-3.5" /> AI Assist
                    </button>
                  )}

                  {/* Schedule Staggered Send */}
                  <button
                    onClick={handleStaggeredSendClick}
                    disabled={isLoading || (!accessToken && !googleAccessToken) || !subject || !body || !hasBodyContent(body) || !fromEmail || toEmails.length === 0}
                    className="px-4 py-2 bg-white border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-all duration-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    title="Schedule one email per recipient with time gaps to protect domain reputation"
                  >
                    <FiClock className="w-3.5 h-3.5" /> Schedule
                  </button>

                  {/* Send — grows to fill remaining space */}
                  <button
                    onClick={() => {
                      handleSendClick();
                    }}
                    disabled={isLoading || (!accessToken && !googleAccessToken) || !subject || !body || !hasBodyContent(body) || !fromEmail || toEmails.length === 0}
                    className="flex-1 min-w-[140px] px-5 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all duration-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-medium hover:shadow-large transform hover:-translate-y-0.5 disabled:transform-none"
                  >
                    <FiSend className="w-3.5 h-3.5" /> {isLoading ? 'Sending...' : 'Send Email(s)'}
                  </button>
                </div>

                {/* Warnings — only shown after user clicks Send */}
                {showSendWarning && !accessToken && !googleAccessToken && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <span className="text-amber-500 flex-shrink-0 mt-0.5">⚠️</span>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Sign-In Required</p>
                      <p className="text-xs text-amber-700 mt-0.5">Click <strong>"Sign in with Microsoft"</strong> or <strong>"Sign in with Google"</strong> at the top right to authenticate before sending.</p>
                    </div>
                  </div>
                )}
                {showSendWarning && (accessToken || googleAccessToken) && (!subject || !body || !hasBodyContent(body) || !fromEmail || toEmails.length === 0) && (
                  <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-xs font-semibold text-primary-800 mb-1">📝 Please complete these required fields:</p>
                    <ul className="text-xs ml-3 list-disc space-y-0.5 text-primary-700">
                      {!fromEmail && <li>From email address</li>}
                      {toEmails.length === 0 && <li>At least one recipient</li>}
                      {!subject && <li>Email subject</li>}
                      {(!body || !hasBodyContent(body)) && <li>Email body content</li>}
                    </ul>
                  </div>
                )}
              </div>

              {/* Right Column - Templates */}
              <div className="lg:col-span-1">
                <TemplateSelector
                  templates={templates}
                  selectedTemplate={selectedTemplate}
                  onSelectTemplate={handleTemplateSelect}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPreview && (
        <EmailPreview
          recipients={toEmails}
          subject={subject}
          body={body}
          templateId={selectedTemplate?.id}
          fromEmail={fromEmail}
          accessToken={accessToken}
          googleAccessToken={googleAccessToken}
          provider={provider}
          onClose={() => setShowPreview(false)}
          onSendComplete={() => {
            setShowPreview(false);
            toast.success('All emails sent from preview!');
          }}
        />
      )}

      {showAIHelper && (
        <AIEmailHelper
          subject={subject}
          body={body}
          onClose={() => setShowAIHelper(false)}
          onSelectSubject={(s) => setSubject(s)}
          onApplyBody={(html) => {
            setBody(html);
            setShowTemplateEditor(false);
          }}
        />
      )}

      {/* Contacts Selection Modal */}
      {showContactsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FiUsers className="w-5 h-5 text-primary-600" />
                Select Contacts
              </h2>
              <button onClick={() => setShowContactsModal(false)} className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-neutral-100">
              <input
                type="text"
                value={contactsSearch}
                onChange={(e) => setContactsSearch(e.target.value)}
                placeholder="Search contacts..."
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none text-sm"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {contacts.length === 0 ? (
                <div className="text-center py-8 text-neutral-500 text-sm">
                  <FiUsers className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No contacts found. Add contacts from the Contacts page.
                </div>
              ) : (
                <div className="space-y-2">
                  {contacts
                    .filter((c) =>
                      (c.name || '').toLowerCase().includes(contactsSearch.toLowerCase()) ||
                      (c.email || '').toLowerCase().includes(contactsSearch.toLowerCase()) ||
                      (c.company || '').toLowerCase().includes(contactsSearch.toLowerCase())
                    )
                    .map((c) => (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${selectedContactIds.has(c.id) ? 'bg-primary-50 border-primary-300' : 'bg-white border-neutral-200 hover:bg-neutral-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedContactIds.has(c.id)}
                          onChange={() => toggleContactSelection(c.id)}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-neutral-900 truncate">{c.name || c.email}</div>
                          <div className="text-xs text-neutral-500 truncate">{c.email}{c.company ? ` · ${c.company}` : ''}</div>
                        </div>
                      </label>
                    ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
              <span className="text-sm text-neutral-600">
                {selectedContactIds.size} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowContactsModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={applySelectedContacts}
                  disabled={selectedContactIds.size === 0}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <FiSend className="w-4 h-4" />
                  Add to Recipients
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Staggered Schedule Modal */}
      {showStaggeredModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary-100 rounded-full">
                <FiClock className="w-6 h-6 text-primary-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Staggered Schedule</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Create {toEmails.length} individual schedule{toEmails.length !== 1 ? 's' : ''} spaced apart to protect your sender reputation.
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={staggeredStart}
                  onChange={(e) => setStaggeredStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interval (minutes)</label>
                <input
                  type="number"
                  min={0}
                  value={staggeredInterval}
                  onChange={(e) => setStaggeredInterval(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Recommended: 5–15 minutes between emails. Set to 0 to send all emails at once.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Recipients</span><span className="font-medium text-gray-900">{toEmails.length}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">First email</span><span className="font-medium text-gray-900">{staggeredStart ? new Date(staggeredStart).toLocaleString() : '—'}</span></div>
                {staggeredInterval > 0 ? (
                  <div className="flex justify-between"><span className="text-gray-500">Last email</span><span className="font-medium text-gray-900">{staggeredStart ? new Date(new Date(staggeredStart).getTime() + (toEmails.length - 1) * staggeredInterval * 60000).toLocaleString() : '—'}</span></div>
                ) : (
                  <div className="flex justify-between"><span className="text-gray-500">Sending mode</span><span className="font-medium text-amber-600">All at once</span></div>
                )}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowStaggeredModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStaggeredSend}
                disabled={isLoading || !staggeredStart}
                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {isLoading ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiClock className="w-4 h-4" />}
                {isLoading ? 'Scheduling...' : `Schedule ${toEmails.length} Email${toEmails.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <FiSend className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Confirm Send</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Please review the details below. You will have <strong>{recallWindowSeconds} seconds</strong> to recall the email after confirming.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm mb-6">
              <div className="flex justify-between">
                <span className="text-gray-500">From</span>
                <span className="font-medium text-gray-900">{fromEmail}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Recipients</span>
                <span className="font-medium text-gray-900">{toEmails.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Provider</span>
                <span className="font-medium text-gray-900 capitalize">{provider}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Time Zone</span>
                <span className="font-medium text-gray-900">{timezone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Subject</span>
                <span className="font-medium text-gray-900 truncate max-w-xs">{subject}</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowConfirmModal(false); handleConfirmSend(); }}
                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all text-sm font-medium flex items-center gap-2"
              >
                <FiSend className="w-4 h-4" /> Yes, Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailComposer;
